import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faCat,
  faDog,
  faHorse,
  faOtter,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";

// This is the header component for Campaign Search page
// Renders different searches and dropdowns depending on the campaign type

// Component for advanced search with all inputs in one row
const AdvancedSearch = ({
  values,
  handleChange,
  handleSearch,
  handleKeyDown,
  placeHolder,
  states,
  ageOptions,
  thirdFieldName = "age",
  thirdFieldLabel = "Age",
  thirdFieldPlaceholder = "Select an age",
}) => {
  return (
    <div
      className="bg-white rounded-full px-6 py-1 shadow-lg w-full"
      style={{ maxWidth: "996px" }}
    >
      <div className="flex items-center justify-between gap-6">
        <div className="w-1/2 min-w-0">
          <div className="!text-lg !font-montserrat !font-semibold text-left ml-3 -mb-3 mt-1  ">
            Advanced Search
          </div>
          <input
            type="text"
            placeholder={placeHolder}
            name="search"
            value={values.search}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            className="w-full border-none outline-none !text-lg !font-montserrat  text-[#7C7C7C] placeholder-[#7C7C7C] bg-transparent"
          />
        </div>

        <div className="w-px bg-gray-400 h-12"></div>

        <div className="w-1/4 min-w-0">
          <label
            htmlFor="state"
            className="!text-lg !font-montserrat !font-semibold text-left "
          >
            State
          </label>
          <div className="relative">
            <select
              name="state"
              value={values.state}
              onChange={handleChange}
              className="border-none outline-none shadow-none bg-transparent !text-lg !font-montserrat text-[#7C7C7C] p-0 m-0 appearance-none w-full cursor-pointer"
              id="state"
            >
              <option value="" disabled className="text-gray-400">
                Select a state
              </option>
              {states.map((state) => (
                <option
                  key={state.abbreviation}
                  value={state.abbreviation}
                  className="text-black"
                >
                  {state.name}
                </option>
              ))}
            </select>
            <FontAwesomeIcon
              icon={faChevronDown}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400 text-xs"
            />
          </div>
        </div>

        <div className="w-px bg-gray-200 h-6"></div>

        <div className="w-1/4 min-w-0">
          <label
            htmlFor={thirdFieldLabel}
            className="!text-lg !font-montserrat !font-semibold text-left"
          >
            {thirdFieldLabel}
          </label>
          <div className="relative">
            <select
              name={thirdFieldName}
              value={values[thirdFieldName]}
              onChange={handleChange}
              className="border-none outline-none shadow-none bg-transparent !text-lg !font-montserrat text-[#7C7C7C] p-0 m-0 appearance-none w-full cursor-pointer"
              id={thirdFieldLabel}
            >
              <option value="" disabled className="text-gray-400">
                {thirdFieldPlaceholder}
              </option>
              {ageOptions.map((option, index) => (
                <option
                  key={index}
                  value={option.value || option}
                  className="text-black"
                >
                  {option.label || option}
                </option>
              ))}
            </select>
            <FontAwesomeIcon
              icon={faChevronDown}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400 text-xs"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const SearchHeader = ({
  handleSearchChange,
  animalSearch,
  ageOptions,
  productSearch,
  productTypeOptions,
  capitalSearch,
  projectStatusOptions,
  type,
  handleSearch,
  handleKeyDown,
  stateParam,
  states,
}) => {
  {
    /* Icon map for animal search */
  }
  const speciesIconMap = {
    dog: faDog,
    cat: faCat,
    farm: faHorse,
    exotic: faOtter,
  };

  return (
    <div className="sm:grow flex flex-col justify-center items-center w-full !my-0 pt-40 pb-20 bg-blue">
      {type === "animal" ? (
        <>
          <h1 className="text-white !font-poppins !text-5xl !font-medium !leading-normal mb-14">
            Animal Campaigns
          </h1>
          <div className="flex gap-6 lg:gap-12 mb-11 ">
            {["", "dog", "cat", "farm", "exotic"].map((species) => (
              <label
                key={species}
                className={`flex flex-col items-center cursor-pointer ${
                  animalSearch.species === species
                    ? "text-blue-navy"
                    : "text-white"
                }`}
              >
                <input
                  className="opacity-0 w-0 h-0"
                  type="radio"
                  name="species" // Set a fixed name for consistent state updates
                  id={`species-${species || "all"}`}
                  value={species} // Use `species` directly for the value
                  defaultChecked={species === ""}
                  onChange={handleSearchChange}
                />
                <FontAwesomeIcon
                  className="text-4xl "
                  icon={speciesIconMap[species] || faStar} // Sets the icon based on the species or just set a star icon
                />
                <p className="m-0 mt-3.5 capitalize text-center !font-poppins !text-xl !font-bold ">
                  {species || "All"}
                </p>
              </label>
            ))}
          </div>

          <AdvancedSearch
            values={animalSearch}
            handleChange={handleSearchChange}
            handleSearch={handleSearch}
            handleKeyDown={handleKeyDown}
            placeHolder="Find city, name, nonprofit"
            states={states}
            ageOptions={ageOptions}
          />
        </>
      ) : type === "product" ? (
        <>
          <h1 className="text-white !font-poppins !text-5xl !font-medium !leading-normal mb-14">
            Product Campaigns
          </h1>
          <AdvancedSearch
            values={productSearch}
            handleChange={handleSearchChange}
            handleSearch={handleSearch}
            handleKeyDown={handleKeyDown}
            placeHolder="Find city, name, nonprofit"
            states={states}
            ageOptions={productTypeOptions}
            thirdFieldName="productType"
            thirdFieldLabel="Product Type"
            thirdFieldPlaceholder="Select a product"
          />
        </>
      ) : type === "capital" ? (
        <div>
          <h1 className="text-white !font-poppins !text-5xl !font-medium !leading-normal mb-14">
            Capital Campaigns
          </h1>
          <AdvancedSearch
            values={capitalSearch}
            handleChange={handleSearchChange}
            handleSearch={handleSearch}
            handleKeyDown={handleKeyDown}
            placeHolder="Find city, name, nonprofit"
            states={states}
            ageOptions={projectStatusOptions}
            thirdFieldName="projectStatus"
            thirdFieldLabel="Project Status"
            thirdFieldPlaceholder="Select a status"
          />
        </div>
      ) : null}
    </div>
  );
};

export default SearchHeader;
